import { useState } from 'react';

import { Dialog, DialogContent, Step, Stepper } from '@opositandopn/ui';
import classnames from 'classnames';
import {
  Formik,
  FormikErrors,
  FormikProps,
  validateYupSchema,
  yupToFormErrors,
} from 'formik';
import * as Yup from 'yup';

import { StepForm } from '../../../../components/shared/admin/layout/StepForm';
import { StepperHeader } from '../../../../components/shared/admin/layout/StepperHeader';
import { BaseComponentProps } from '../../../../types/base-component.types';
import { DifficultyLevel } from '../../../questions/api/useMultipleChoiceQuestions';
import {
  TrainingPlanViewModel,
  useCreateTrainingPlan,
  useDeleteTrainingPlan,
  useUpdateTrainingPlan,
} from '../../api/usePhysicalTests';
import { TrainingPlanReviewStep } from './steps/TrainingPlanReviewStep';
import { TrainingPlanStep } from './steps/TrainingPlanStep';

import styles from './TrainingPlanEditor.module.scss';

export interface TrainingPlanFormValues {
  id?: string;
  name: string;
  description: string;
  difficultyLevel: DifficultyLevel;
}

type ContainerProps = BaseComponentProps & {
  open?: boolean;
  trainingPlan?: TrainingPlanViewModel;
  onClose: () => void;
};

function TrainingPlanEditor({
  className,
  open,
  trainingPlan,
  onClose,
  testId = 'ui-training-plan-editor',
}: ContainerProps) {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [steps, setSteps] = useState<Step[]>([
    { variant: 'active', label: 'Contenido' },
    { variant: 'disabled', label: 'Revisar y publicar' },
  ]);

  const { mutateAsync: createTrainingPlan } = useCreateTrainingPlan();
  const { mutateAsync: updateTrainingPlan } = useUpdateTrainingPlan();
  const { mutate: deleteTrainingPlan, isPending: isDeletingTrainingPlan } =
    useDeleteTrainingPlan();

  const onNext = () => {
    setActiveStep(activeStep + 1);
    setSteps(
      steps.map((step, index) => {
        if (index === activeStep) {
          return { ...step, variant: 'completed' };
        }

        if (index === activeStep + 1) {
          return { ...step, variant: 'active' };
        }

        return step;
      }),
    );
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className={classnames(styles['main'], className)}
        data-testid={testId}
      >
        <div className={styles['edit']}>
          <StepperHeader>
            <Stepper
              className={styles['stepper']}
              activeStep={activeStep}
              showIcons={true}
              steps={steps}
              onClickStep={(index) => {
                setActiveStep(index);
                setSteps(
                  steps.map((step, stepIndex) => {
                    if (stepIndex === index) {
                      return { ...step, variant: 'active' };
                    }

                    if (stepIndex > index) {
                      return { ...step, variant: 'disabled' };
                    }

                    return step;
                  }),
                );
              }}
            />
          </StepperHeader>
          <Formik
            validate={async (values: TrainingPlanFormValues) => {
              const errors: FormikErrors<TrainingPlanFormValues> = {};

              const validationSchema = Yup.object().shape({
                name: Yup.string()
                  .min(3, 'El nombre debe tener al menos 3 caracteres.')
                  .max(20, 'El nombre no debe superar los 20 caracteres.')
                  .required('El nombre no puede estar vacío.'),
                description: Yup.string()
                  .min(3, 'La descripción debe tener al menos 3 caracteres.')
                  .max(
                    15000,
                    'La descripción no debe superar los 15000 caracteres.',
                  )
                  .required('La descripción no puede estar vacía.'),
                difficultyLevel: Yup.string()
                  .oneOf(['easy', 'medium', 'hard'])
                  .required('Selecciona una dificultad.'),
              });

              try {
                validateYupSchema<TrainingPlanFormValues>(
                  values,
                  validationSchema,
                  true,
                );
              } catch (err) {
                return yupToFormErrors(err);
              }

              return errors;
            }}
            onSubmit={async (values, { resetForm, setSubmitting }) => {
              setSubmitting(true);

              if (trainingPlan) {
                await updateTrainingPlan(
                  {
                    trainingPlanId: trainingPlan.id,
                    name: values.name,
                    description: values.description,
                    difficultyLevel: values.difficultyLevel,
                  },
                  {
                    onSuccess: () => {
                      onClose();
                      resetForm();
                      setActiveStep(0);
                    },
                  },
                );
                return;
              }

              await createTrainingPlan(
                {
                  name: values.name,
                  description: values.description,
                  difficultyLevel: values.difficultyLevel,
                },
                {
                  onSuccess: () => {
                    onClose();
                    resetForm();
                    setActiveStep(0);
                  },
                },
              );

              setSubmitting(false);
            }}
            initialValues={{
              id: trainingPlan?.id,
              name: trainingPlan?.name ?? '',
              description: trainingPlan?.description ?? '',
              difficultyLevel: trainingPlan?.difficultyLevel ?? 'easy',
            }}
          >
            {({ resetForm }: FormikProps<TrainingPlanFormValues>) => (
              <StepForm>
                {activeStep === 0 && (
                  <TrainingPlanStep
                    onNext={onNext}
                    onCancel={() => {
                      onClose();
                      resetForm();
                      setActiveStep(0);
                    }}
                    isDeleting={isDeletingTrainingPlan}
                    onDelete={() => {
                      if (trainingPlan) {
                        deleteTrainingPlan(
                          {
                            trainingPlanId: trainingPlan.id,
                          },
                          {
                            onSuccess: () => {
                              onClose();
                              resetForm();
                              setActiveStep(0);
                            },
                          },
                        );
                      }
                    }}
                  />
                )}

                {activeStep === 1 && (
                  <TrainingPlanReviewStep
                    onCancel={() => {
                      onClose();
                      resetForm();
                      setActiveStep(0);
                    }}
                  />
                )}
              </StepForm>
            )}
          </Formik>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { TrainingPlanEditor };
